import { z } from 'zod';

import { AccountRefSchema } from '../../users/account-ref/account-ref';
import { CampaignSegmentSchema } from '../campaign-segment/campaign-segment';

/** How many matched members the preview names. Enough to recognise a mistake. */
export const CAMPAIGN_SEGMENT_SAMPLE_SIZE = 10;

export const CampaignSegmentPreviewRequestSchema = z
  .object({ segment: CampaignSegmentSchema })
  .strict();

export type CampaignSegmentPreviewRequest = z.infer<typeof CampaignSegmentPreviewRequestSchema>;

/**
 * What a segment resolves to **right now** — the answer that has to be in front
 * of a human before anything irreversible happens.
 *
 * A count alone is not enough. A count is equally consistent with the right
 * segment and with a filter that silently narrowed nothing, and the difference
 * between those two is the whole phase. So the preview also names some of the
 * people, and says plainly when the segment is everybody.
 */
export const CampaignSegmentPreviewSchema = z
  .object({
    /**
     * Deliberately *estimated*: the segment is resolved again, member by member,
     * as the walk runs. The name is the field's own disclaimer, so a screen
     * cannot present it as the number that will be sent to.
     */
    estimatedCount: z.number().int().min(0),
    /**
     * Some of the matched accounts — or **null** when the reader may not see
     * them.
     *
     * Resolving members is `members.read`, a different permission from the
     * `notifications.read` that opens this screen, so a support role gets the
     * count and a sentence saying why there is no sample, rather than a list of
     * uuids or a request guaranteed to fail. The same reasoning the delivery
     * log's member filter already follows, applied server-side because that is
     * where a campaign's sample has to be resolved.
     */
    sample: z.array(AccountRefSchema).nullable(),
    /** True when nothing narrows the population. The screen must say so. */
    isEveryone: z.boolean(),
  })
  .strict();

export type CampaignSegmentPreview = z.infer<typeof CampaignSegmentPreviewSchema>;
